import React, { forwardRef } from 'react'

import { Xmark } from '@gravity-ui/icons'
import { Calendar, cn, DateField, DatePicker, I18nProvider, Label } from '@heroui/react'
import { CalendarDate, parseDate } from '@internationalized/date'

type DateInputProps = {
  label?: string;
  value?: string;
  onChange?: (val: string) => void;
  className?: string;
  required?: boolean;
  isRequired?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isInvalid?: boolean;
  errorMessage?: string;
  onBlur?: React.FocusEventHandler;
  name?: string;
  isClearable?: boolean;
};

export const DateInput = forwardRef<HTMLDivElement, DateInputProps>(({
  label,
  value,
  onChange,
  className,
  required,
  isRequired,
  variant = 'secondary',
  isInvalid,
  onBlur,
  name,
  isClearable,
}, ref) => {

  // Convert string (YYYY-MM-DD) to CalendarDate
  const dateValue = React.useMemo(() => {
    if (!value) return null
    try {
      return parseDate(value as string)
    } catch {
      return null
    }
  }, [value])

  const handleChange = (date: CalendarDate | null) => {
    if (typeof onChange === 'function') {
      onChange(date ? date.toString() : '')
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (typeof onChange === 'function') {
      onChange('')
    }
  }

  return (
    <I18nProvider locale={'ru-RU'}>
      <DatePicker
        ref={ref}
        className={cn('w-full', className as any)}
        value={dateValue}
        onChange={handleChange}
        isRequired={required || isRequired}
        isInvalid={isInvalid}
        onBlur={onBlur as any}
        name={name}
      >
        {label && (<Label>{label as any}</Label>)}

        <DateField.Group fullWidth variant={variant as any}>
          <DateField.Input>
            {segment => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <DateField.Suffix>
            {isClearable && value && (
              <button
                type="button"
                onClick={handleClear}
                className="cursor-pointer text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors p-0.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1 flex items-center justify-center"
                aria-label="Clear date"
              >
                <Xmark className="size-3.5" />
              </button>
            )}
            <DatePicker.Trigger>
              <DatePicker.TriggerIndicator />
            </DatePicker.Trigger>
          </DateField.Suffix>
        </DateField.Group>

        <DatePicker.Popover>
          <Calendar aria-label="Event date">
            <Calendar.Header>
              <Calendar.YearPickerTrigger>
                <Calendar.YearPickerTriggerHeading />
                <Calendar.YearPickerTriggerIndicator />
              </Calendar.YearPickerTrigger>
              <Calendar.NavButton slot="previous" />
              <Calendar.NavButton slot="next" />
            </Calendar.Header>
            <Calendar.Grid>
              <Calendar.GridHeader>
                {day => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
              </Calendar.GridHeader>
              <Calendar.GridBody>
                {date => <Calendar.Cell date={date} />}
              </Calendar.GridBody>
            </Calendar.Grid>
            <Calendar.YearPickerGrid>
              <Calendar.YearPickerGridBody>
                {({ year }) => <Calendar.YearPickerCell year={year} />}
              </Calendar.YearPickerGridBody>
            </Calendar.YearPickerGrid>
          </Calendar>
        </DatePicker.Popover>
      </DatePicker>
    </I18nProvider>
  )
})

DateInput.displayName = 'DateInput'
